import { FormCriteriaInput } from "@/shared/ui/FormCriteriaInput";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormInput,
  FormNumber,
  FormTextarea,
  Label,
  RadioGroup,
  RadioGroupItem,
  RHF,
  type RHF as RHFType,
} from "@devhop/ui";
import type { KPIFormValues } from "./KPIForm";

interface KPIFormSectionsProps {
  formMethods: RHFType.UseFormReturn<KPIFormValues, unknown, KPIFormValues>;
  isEdit?: boolean;
}

export function KPIFormSections({
  formMethods,
  isEdit = false,
}: KPIFormSectionsProps) {
  return (
    <div className="space-y-8">
      {/* Section 1: ข้อมูล KPI */}
      <div className="space-y-4">
        <div>
          <h2 className="font-semibold text-lg">ສ່ວນທີ 1: ຂໍ້ມູນ KPI</h2>
          <p className="text-muted-foreground text-sm">ກຳນົດຊື່ແລະລະດັບການວັດແທກ</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ຂໍ້ມູນພື້ນຖານ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div data-tourid="form-name">
              <FormInput
                name="name"
                label="ຊື່ KPI"
                requiredMark
                placeholder="ຕົວຢ່າງ: ຍອດຂາຍລາຍເດືອນ, ອັດຕາການມາຮອດຂອງພະນັກງານ"
              />
            </div>
            <RHF.Controller
              name="level"
              control={formMethods.control}
              render={({ field }) => (
                <div className="space-y-2" data-tourid="form-level">
                  <Label className="font-medium text-sm">
                    ລະດັບ <span className="text-destructive">*</span>
                  </Label>
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex flex-wrap gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="employee"
                        id="level-employee-section"
                      />
                      <Label
                        htmlFor="level-employee-section"
                        className="cursor-pointer font-normal text-sm"
                      >
                        ພະນັກງານ
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="position"
                        id="level-position-section"
                      />
                      <Label
                        htmlFor="level-position-section"
                        className="cursor-pointer font-normal text-sm"
                      >
                        ຕຳແໜ່ງ
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="department"
                        id="level-department-section"
                      />
                      <Label
                        htmlFor="level-department-section"
                        className="cursor-pointer font-normal text-sm"
                      >
                        ພະແນກ
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="organization"
                        id="level-organization-section"
                      />
                      <Label
                        htmlFor="level-organization-section"
                        className="cursor-pointer font-normal text-sm"
                      >
                        ອົງກອນ
                      </Label>
                    </div>
                  </RadioGroup>
                  <p className="text-muted-foreground text-xs">
                    ເລືອກວ່າ KPI ນີ້ຈະວັດແທກລະດັບໃດ
                  </p>
                </div>
              )}
            />
            <div data-tourid="form-description">
              <FormTextarea
                name="description"
                label="ວັດແທກແນວໃດ"
                placeholder="ຕົວຢ່າງ: ວັດຈາກຍອດຂາຍລວມຂອງພະແນກ, ນັບຈາກຈຳນວນຄູ່ມືທີ່ໄດ້ຮັບ"
                rows={3}
              />
            </div>
            <p className="-mt-2 text-muted-foreground text-xs">
              ອະທິບາຍແບບງ່າຍໆວ່າ KPI ນີ້ວັດແທກຫຍັງ (ບໍ່ຈຳເປັນຕ້ອງກະລຸນາ)
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Section 2: วิธีวัด */}
      <div className="space-y-4">
        <div>
          <h2 className="font-semibold text-lg">ສ່ວນທີ 2: ວິທີການວັດ</h2>
          <p className="text-muted-foreground text-sm">
            ອະທິບາຍວິທີການວັດແທກແບບງ່າຍໆ, ຫຼີກເລີກສູດທີ່ສັບຊ້ອນ
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ລາຍລະອຽດການວັດແທກ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div data-tourid="form-measurement-source">
                <FormInput
                  name="measurementSource"
                  label="ວັດຈາກຫຍັງ"
                  placeholder="ຕົວຢ່າງ: ຈາກ Checklist, ຈາກລະບົບຂາຍ, ຈາກການນັບດ້ວຍຕົນເອງ"
                  hint="ບໍ່ຈຳເປັນຕ້ອງກະລຸນາ - ຖ້າບໍ່ຊັດເຈນສາມາດຂ້າມໄດ້"
                />
              </div>

              <RHF.Controller
                name="measurementPeriod"
                control={formMethods.control}
                render={({ field }) => (
                  <div
                    className="space-y-2"
                    data-tourid="form-measurement-period"
                  >
                    <Label className="font-medium text-sm">
                      ຮອບການວັດ <span className="text-destructive">*</span>
                    </Label>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex flex-wrap gap-6"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="monthly"
                          id="measurement-period-monthly-section"
                        />
                        <Label
                          htmlFor="measurement-period-monthly-section"
                          className="cursor-pointer font-normal text-sm"
                        >
                          ເດືອນ
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="quarterly"
                          id="measurement-period-quarterly-section"
                        />
                        <Label
                          htmlFor="measurement-period-quarterly-section"
                          className="cursor-pointer font-normal text-sm"
                        >
                          ໄຕມາດ
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="yearly"
                          id="measurement-period-yearly-section"
                        />
                        <Label
                          htmlFor="measurement-period-yearly-section"
                          className="cursor-pointer font-normal text-sm"
                        >
                          ປີ
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                )}
              />
            </div>

            <div data-tourid="form-measurement-method">
              <FormTextarea
                name="measurementMethod"
                label="ນັບຢ່າງໃດ"
                requiredMark
                placeholder="ຕົວຢ່າງ: ນັບຈາກຍອດຂາຍທັງໝົດຂອງພະແນກໃນແຕ່ລະເດືອນ"
                rows={4}
                hint="ອະທິບາຍວິທີການນັບຫຼືຄິດໄລ່ແບບງ່າຍໆ, ຫຼີກລ້ຽງສູດສັບຊ້ອນ"
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 3: เป้าหมาย */}
      <div className="space-y-4">
        <div>
          <h2 className="font-semibold text-lg">ສ່ວນທີ 3: ເປົ້າໝາຍ</h2>
          <p className="text-muted-foreground text-sm">
            ກຳນົດເປົ້າໝາຍແລະເງື່ອນໄຂການປະເມີນ
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ກຳນົດເປົ້າໝາຍ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div data-tourid="form-target-value">
                <FormNumber
                  name="targetValue"
                  label="ຄ່າເປົ້າໝາຍ"
                  placeholder="ຕົວຢ່າງ: 100"
                  hint="ບໍ່ຈຳເປັນຕ້ອງກະລຸນາ - ສາມາດກຳນົດໃຫ້ທັນເວລາໄດ້"
                />
              </div>
              <RHF.Controller
                name="targetUnit"
                control={formMethods.control}
                render={({ field }) => (
                  <div
                    className="space-y-2 md:col-span-2"
                    data-tourid="form-target-unit"
                  >
                    <Label className="font-medium text-sm">
                      ຫົວໜ່ວຍຂອງຄ່າເປົ້າໝາຍ
                    </Label>
                    <RadioGroup
                      value={field.value ?? ""}
                      onValueChange={(val) => field.onChange(val || null)}
                      className="flex flex-wrap gap-3"
                    >
                      <div className="flex items-center">
                        <RadioGroupItem
                          value="percent"
                          id="target-unit-percent-section"
                          className="sr-only"
                        />
                        <Label
                          htmlFor="target-unit-percent-section"
                          className="cursor-pointer"
                        >
                          <Badge
                            variant={
                              field.value === "percent" ? "default" : "outline"
                            }
                            className="px-3 py-1.5 font-normal text-sm transition-all hover:bg-accent hover:text-accent-foreground"
                          >
                            ເປີເຊັນ (%)
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <RadioGroupItem
                          value="count"
                          id="target-unit-count-section"
                          className="sr-only"
                        />
                        <Label
                          htmlFor="target-unit-count-section"
                          className="cursor-pointer"
                        >
                          <Badge
                            variant={
                              field.value === "count" ? "default" : "outline"
                            }
                            className="px-3 py-1.5 font-normal text-sm transition-all hover:bg-accent hover:text-accent-foreground"
                          >
                            ຈຳນວນຄັ້ງ
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <RadioGroupItem
                          value="score"
                          id="target-unit-score-section"
                          className="sr-only"
                        />
                        <Label
                          htmlFor="target-unit-score-section"
                          className="cursor-pointer"
                        >
                          <Badge
                            variant={
                              field.value === "score" ? "default" : "outline"
                            }
                            className="px-3 py-1.5 font-normal text-sm transition-all hover:bg-accent hover:text-accent-foreground"
                          >
                            ຄະແນນ (1-10)
                          </Badge>
                        </Label>
                      </div>
                      <div className="flex items-center">
                        <RadioGroupItem
                          value="currency"
                          id="target-unit-currency-section"
                          className="sr-only"
                        />
                        <Label
                          htmlFor="target-unit-currency-section"
                          className="cursor-pointer"
                        >
                          <Badge
                            variant={
                              field.value === "currency" ? "default" : "outline"
                            }
                            className="px-3 py-1.5 font-normal text-sm transition-all hover:bg-accent hover:text-accent-foreground"
                          >
                            ເງິນ ($)
                          </Badge>
                        </Label>
                      </div>
                      {/* <div className="flex items-center">
                      <RadioGroupItem
                        value="none"
                        id="target-unit-none-section"
                        className="sr-only"
                      />
                      <Label
                        htmlFor="target-unit-none-section"
                        className="cursor-pointer"
                      >
                        <Badge
                          variant={
                            field.value === "none" ? "default" : "outline"
                          }
                          className="px-3 py-1.5 font-normal text-sm transition-all hover:bg-accent hover:text-accent-foreground"
                        >
                          ບໍ່ມີຫົວໜ່ວຍ
                        </Badge>
                      </Label>
                    </div> */}
                    </RadioGroup>
                    <p className="text-muted-foreground text-xs">
                      ເລືອກຫົວໜ່ວຍຂອງຄ່າເປົ້າໝາຍເພື່ອໃຫ້ສອດຄ່ອງກັບກຳນົດການປະເມີນ
                    </p>
                  </div>
                )}
              />
            </div>

            {/* Evaluation Logic Section */}
            <div className="space-y-4 border-t pt-6">
              <div>
                <h3 className="font-medium text-sm">ເງື່ອນໄຂການປະເມີນ</h3>
                <p className="text-muted-foreground text-xs">
                  ກຳນົດເງື່ອນໄຂການຜ່ານຫຼືບໍ່ຜ່ານ (ບໍ່ຈຳເປັນຕ້ອງກະລຸນາ)
                </p>
              </div>
              <div className="space-y-6">
                <div data-tourid="form-pass-criteria">
                  <FormCriteriaInput
                    name="passCriteria"
                    label="ກຳນົດຜ່ານ"
                    targetUnit={formMethods.watch("targetUnit")}
                    placeholder="ຕົວຢ່າງ: >= 80%"
                  />
                </div>
                <div data-tourid="form-fail-criteria">
                  <FormCriteriaInput
                    name="failCriteria"
                    label="ກຳນົດບໍ່ຜ່ານ"
                    targetUnit={formMethods.watch("targetUnit")}
                    placeholder="ຕົວຢ່າງ: < 60%"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 4: หมายเหตุ */}
      <div className="space-y-4">
        <div>
          <h2 className="font-semibold text-lg">ສ່ວນທີ 4: ໝາຍເຫດ</h2>
          <p className="text-muted-foreground text-sm">
            ຂໍ້ຄວາມເຕືອນ, ສິ່ງທີ່ບໍ່ເອົາມານັບ, ແລະຂໍ້ມູນເພີ່ມເຕີມ
          </p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ຂໍ້ມູນເພີ່ມເຕີມ</CardTitle>
          </CardHeader>
          <CardContent>
            <div data-tourid="form-notes">
              <FormTextarea
                name="notes"
                label="ຫມາຍເຫດ"
                placeholder="ຂໍ້ຄວາມເຕືອນ ຫຼື ສິ່ງທີ່ບໍ່ເອົາມານັບ"
                rows={4}
              />
            </div>
            <p className="mt-1 text-muted-foreground text-xs">
              ບໍ່ຈຳເປັນຕ້ອງກະລຸນາ - ໃຊ້ສຳລັບຂໍ້ຄວາມເຕືອນແລະຂໍ້ມູນເພີ່ມເຕີມ
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Status (only for edit) */}
      {isEdit && (
        <div className="space-y-4">
          <div>
            <h2 className="font-semibold text-lg">ສະຖານະ</h2>
          </div>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">ການຈັດການສະຖານະ</CardTitle>
            </CardHeader>
            <CardContent>
              <div data-tourid="form-status">
                <label
                  htmlFor="status"
                  className="mb-2 block font-medium text-sm"
                >
                  ສະຖານະ
                </label>
                <RHF.Controller
                  name="status"
                  control={formMethods.control}
                  render={({ field }) => (
                    <SimpleSelect
                      value={field.value || "draft"}
                      onValueChange={field.onChange}
                      options={[
                        { value: "active", label: "ກຳລັງໃຊ້ງານ" },
                        { value: "draft", label: "ຮ່າງ" },
                        { value: "inactive", label: "ປິດການໃຊ້ງານ" },
                      ]}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
